/**
 * Lattice geometry of a visual field pattern, derived from the true test
 * location degrees rather than from per-eye row-padding tables.
 */

export type AngleCoordinate = [number, number];

export interface VfaMapPoint {
  /** Index of this location in the test's threshold/deviation value arrays. */
  valueIndex: number;
  xDegrees: number;
  yDegrees: number;
  /** Where the printed value sits, which a pattern's print layout may set apart from the location. */
  valueXDegrees: number;
  valueYDegrees: number;
  cellSpanDegrees: number;
  offLattice: boolean;
}

export type DegreeRange = [from: number, to: number];

/** Stretches of each crosshair line a value map leaves undrawn, so values set in them stay clear. */
export interface CrosshairGaps {
  horizontal: DegreeRange[];
  vertical: DegreeRange[];
}

/** A pattern's printed arrangement where it departs from one value per location on a plain crosshair. */
export interface VfaPrintLayout {
  /** Shift of a printed value from its location, in degrees, keyed by value index. */
  valueOffsets?: Record<string, [number, number] | number[]>;
  crosshairGaps?: CrosshairGaps | { horizontal: number[][]; vertical: number[][] };
}

/** Outer edge of the cell lattice, in degrees. */
export interface VfaMapExtent {
  leftDegrees: number;
  rightDegrees: number;
  topDegrees: number;
  bottomDegrees: number;
}

export interface VfaMapGeometry {
  points: VfaMapPoint[];
  columnDegrees: number[];
  rowDegrees: number[];
  /** Spacing between neighbouring lattice locations. */
  cellSpanDegrees: number;
  extent: VfaMapExtent;
  crosshairGaps: CrosshairGaps | null;
}

function uniqueSorted(values: number[], compare: (a: number, b: number) => number): number[] {
  return [...new Set(values)].sort(compare);
}

function smallestGap(sortedValues: number[]): number {
  let smallest = Infinity;
  for (let index = 1; index < sortedValues.length; index++) {
    smallest = Math.min(smallest, Math.abs(sortedValues[index] - sortedValues[index - 1]));
  }
  return smallest;
}

function smallestSpacing(coordinates: AngleCoordinate[]): number {
  return Math.min(
    smallestGap(
      uniqueSorted(
        coordinates.map(([xDegrees]) => xDegrees),
        (a, b) => a - b,
      ),
    ),
    smallestGap(
      uniqueSorted(
        coordinates.map(([, yDegrees]) => yDegrees),
        (a, b) => a - b,
      ),
    ),
  );
}

/** The lattice sets columns, rows, cell size and extent; `offLatticeIndexes` get the finer spacing as their cell. */
export function buildVfaMapGeometry(
  angleCoordinates: AngleCoordinate[],
  offLatticeIndexes: number[] = [],
  printLayout: VfaPrintLayout = {},
): VfaMapGeometry {
  const offLattice = new Set(offLatticeIndexes);
  const latticeCoordinates = angleCoordinates.filter((_, index) => !offLattice.has(index));
  const columnDegrees = uniqueSorted(
    latticeCoordinates.map(([xDegrees]) => xDegrees),
    (a, b) => a - b,
  );
  const rowDegrees = uniqueSorted(
    latticeCoordinates.map(([, yDegrees]) => yDegrees),
    (a, b) => b - a,
  );

  const cellSpanDegrees = smallestSpacing(latticeCoordinates);
  const offLatticeCellSpanDegrees = smallestSpacing(angleCoordinates);
  const halfCell = cellSpanDegrees / 2;

  return {
    points: angleCoordinates.map(([xDegrees, yDegrees], valueIndex) => {
      const [offsetX, offsetY] = printLayout.valueOffsets?.[String(valueIndex)] ?? [0, 0];
      return {
        valueIndex,
        xDegrees,
        yDegrees,
        valueXDegrees: xDegrees + offsetX,
        valueYDegrees: yDegrees + offsetY,
        cellSpanDegrees: offLattice.has(valueIndex) ? offLatticeCellSpanDegrees : cellSpanDegrees,
        offLattice: offLattice.has(valueIndex),
      };
    }),
    columnDegrees,
    rowDegrees,
    cellSpanDegrees,
    extent: {
      leftDegrees: columnDegrees[0] - halfCell,
      rightDegrees: columnDegrees[columnDegrees.length - 1] + halfCell,
      topDegrees: rowDegrees[0] + halfCell,
      bottomDegrees: rowDegrees[rowDegrees.length - 1] - halfCell,
    },
    crosshairGaps: (printLayout.crosshairGaps as CrosshairGaps | undefined) ?? null,
  };
}

export function buildPatternGeometry(patternConfig: {
  angleCoordinates: AngleCoordinate[] | number[][];
  offLatticeIndexes?: number[];
  printLayout?: VfaPrintLayout;
}): VfaMapGeometry {
  return buildVfaMapGeometry(
    patternConfig.angleCoordinates as AngleCoordinate[],
    patternConfig.offLatticeIndexes,
    patternConfig.printLayout,
  );
}

/** Pieces of a line from `from` to `to` that are left after cutting out the gaps. */
export function lineOutsideGaps(from: number, to: number, gaps: DegreeRange[]): DegreeRange[] {
  const pieces: DegreeRange[] = [];
  let start = from;
  for (const [gapFrom, gapTo] of [...gaps].sort((a, b) => a[0] - b[0])) {
    if (gapTo <= start || gapFrom >= to) continue;
    if (gapFrom > start) pieces.push([start, gapFrom]);
    start = Math.max(start, gapTo);
  }
  if (start < to) pieces.push([start, to]);
  return pieces;
}

/**
 * Degree coordinate of a recorded blind spot response. `angles` carries one
 * horizontal entry (key "0" or "1") and one vertical entry (key "2" or "3");
 * either pair being incomplete means the response can't be placed.
 */
export function getBlindSpotDegrees(
  angles: Record<string, unknown> | undefined,
  eye: string,
): { xDegrees: number; yDegrees: number } | null {
  const horizontalKey = ["0", "1"].find((key) => {
    const value = angles?.[key];
    return typeof value === "number" && Number.isFinite(value);
  });
  const verticalKey = ["2", "3"].find((key) => {
    const value = angles?.[key];
    return typeof value === "number" && Number.isFinite(value);
  });
  if (!horizontalKey || !verticalKey) return null;

  return {
    xDegrees: Number(angles![horizontalKey]) * (eye === "leftEye" ? -1 : 1),
    yDegrees: Number(angles![verticalKey]) * (verticalKey === "3" ? 1 : -1),
  };
}

export function extentWidthDegrees(extent: VfaMapExtent): number {
  return extent.rightDegrees - extent.leftDegrees;
}

export function extentHeightDegrees(extent: VfaMapExtent): number {
  return extent.topDegrees - extent.bottomDegrees;
}

/**
 * The map's content group is y-flipped so that degrees run bottom-up, which
 * puts the top of the field at the most negative SVG y.
 */
export function toFlippedViewBox(extent: VfaMapExtent): string {
  return [
    extent.leftDegrees,
    -extent.topDegrees,
    extentWidthDegrees(extent),
    extentHeightDegrees(extent),
  ].join(" ");
}

/**
 * Places unflipped content — glyphs, symbol artwork, hatch tiles — at a degree
 * coordinate inside the y-flipped map group. The leading counter-flip is what
 * keeps that content the right way up; everything it wraps is then positioned
 * in a y-down frame.
 */
export function placeUnflipped(xDegrees: number, yDegrees: number, scale = 1): string {
  return `scale(1,-1) translate(${xDegrees} ${-yDegrees}) scale(${scale})`;
}
